import React, { type SetStateAction, type Dispatch, type FC } from 'react';
import { Link } from 'react-router-dom';
import type { TrackingProp } from 'react-tracking';
import Address from '@ynap/address';
import { getFields } from '@ynap/address-form-v1/src/AddressForm/AddressFields';
import BEMHelper from '@ynap/bem-helper';
import type { FormValues, FieldRenderProps } from '@ynap/form';
import { Field as FormField, useFormField } from '@ynap/form';
import Notification from '@ynap/notification';
import { StopPropagation } from '@ynap/overlay';
import { RadioButton } from '@ynap/radio-button';
import { AddressForm } from '../AddressForm';
import { mapAddressValues, useAddressValidateContext } from '../AddressForm/utils';
import type { EditAddressAction, AddAddressAction } from './@types';
import type { Locale, Config, Messages, CheckoutAnalytics } from '../AddressForm/@types';

const bem = new BEMHelper('AddressSelectionForm');

type AnyFn = (...args: any[]) => any;

export interface AddressRadioButtonProps {
    config: Config;
    locale: Locale;
    messages: Messages;
    address: FormValues;
    match: {
        path: string;
        url: string;
    };
    onChange: AnyFn;
    isRestricted?: boolean;
    editAddress?: EditAddressAction;
    addAddress?: AddAddressAction;
    trackEditAddress?: AnyFn;
    defaultAddress?: string;
    isCheckoutLayout?: boolean;
    trackUpdateAddress?: AnyFn;
    setEditAddressID?: AnyFn;
    onSubmit?: (values: FormValues) => any;
    onCancel?: () => void;
    editAddressID?: number | string;
    editable?: boolean;
    setSaveEditing?: AnyFn;
    saveEditing?: number;
    hideCountry?: boolean;
    tracking?: TrackingProp;
    primaryCategory?: string;
    checkoutAnalytics?: CheckoutAnalytics;
    split?: boolean;
    isGuestUser?: boolean;
    reduxForm?: any;
    removeAddressFinder?: AnyFn;
    setRemoveAddressFinder?: Dispatch<SetStateAction<() => any>>;
    invalidFields?: string[];
}

const AddressRadioButton: FC<AddressRadioButtonProps> = ({
    address,
    checkoutAnalytics,
    config,
    defaultAddress,
    editable = true,
    editAddress,
    editAddressID,
    hideCountry,
    invalidFields,
    isCheckoutLayout,
    isGuestUser = false,
    isRestricted,
    locale,
    match,
    messages,
    onCancel,
    onChange: onChangeParent,
    onSubmit,
    primaryCategory,
    reduxForm,
    removeAddressFinder,
    saveEditing,
    setEditAddressID,
    setRemoveAddressFinder,
    setSaveEditing,
    split,
    trackEditAddress,
    tracking,
    trackUpdateAddress,
}) => {
    const [addressIdField, setAddressIdField] = useFormField('addressId');

    const { countries } = useAddressValidateContext();
    const { head } = config;
    const enableLoqateOn = head?.enableLoqateOn?.includes(locale.country);
    const addressFinderEnabled = config.head.loqate?.enabled && enableLoqateOn;
    const fieldsWCS = getFields(countries, (address?.country as string) || locale.country, addressFinderEnabled);
    const stateField = fieldsWCS?.find((fieldWCS) => fieldWCS.wcsType?.name === 'state');

    const handleSubmit = (values: FormValues) => {
        const isSavedStateNameInvalid =
            !!address?.state &&
            !!stateField?.values?.length &&
            !stateField?.mandatory &&
            typeof stateField.values.find((thisVal) => address.state === (typeof thisVal !== 'string' ? thisVal.value : thisVal)) === 'undefined';

        const allowedEmptyValues: string[] = [];

        if (isSavedStateNameInvalid) {
            allowedEmptyValues.push('state');
        }

        if (editAddress) {
            const mappedValues = mapAddressValues(values, locale);
            trackUpdateAddress?.();

            // We are about to edit an address (we only do this if we are in checkoutV3, which here is safeguarded by the existance of setSaveEditing)
            setSaveEditing?.(1);

            return editAddress(mappedValues, allowedEmptyValues).then(({ payload: { addressId } }) => {
                // Need to update the currently selected address
                setAddressIdField({ ...addressIdField, value: addressId });

                if (isCheckoutLayout) {
                    // force cast to number since addressId can come as string from api
                    setSaveEditing?.(Number(addressId));
                    return onSubmit?.({ ...values, addressId }).finally(() => setSaveEditing?.(0)); // reset state after everything is finished
                }
            });
        }
    };

    return (
        <>
            {isCheckoutLayout && editAddressID === address.addressId && (
                <StopPropagation>
                    <AddressForm
                        touched
                        checkoutAnalytics={checkoutAnalytics}
                        config={config}
                        country={locale.country}
                        hideCountry={hideCountry}
                        initialValues={address}
                        invalidFields={invalidFields}
                        isCheckoutLayout={isCheckoutLayout}
                        loading={saveEditing ? saveEditing > 1 : false}
                        messages={messages}
                        onCancel={onCancel}
                        onSubmit={handleSubmit}
                        primaryCategory={primaryCategory}
                        reduxForm={reduxForm}
                        removeAddressFinder={removeAddressFinder}
                        setRemoveAddressFinder={setRemoveAddressFinder}
                        tracking={tracking}
                    />
                </StopPropagation>
            )}
            {(!isCheckoutLayout || editAddressID !== address.addressId) && (
                <FormField key={String(address.addressId)} disabled={isRestricted} name="addressId" value={defaultAddress || ''}>
                    {({ onChange, value, ...rest }: FieldRenderProps) => {
                        // addressId can either be a string or a number so we need to keep the two == so there's type coersion
                        // eslint-disable-next-line
                        const checked = address.addressId == (value == '' ? defaultAddress : value);
                        return (
                            <>
                                <RadioButton
                                    {...rest}
                                    checked={checked}
                                    id={address.addressId}
                                    label={<Address {...address} isCheckoutLayout={isCheckoutLayout} />}
                                    value={address.addressId}
                                    className={bem(
                                        'address',
                                        { disabled: isRestricted, transient: address.transient },
                                        isCheckoutLayout ? 'isCheckoutLayout' : 'overide',
                                    )}
                                    description={
                                        editAddress && !isCheckoutLayout ? (
                                            <Link className={bem('link')} onClick={() => trackEditAddress?.()} to={`${match.url}/edit/${address.addressId}`}>
                                                {messages.addressSelectionForm?.edit?.()}
                                            </Link>
                                        ) : null
                                    }
                                    notification={
                                        address?.transient &&
                                        !split &&
                                        !isGuestUser && (
                                            <Notification
                                                className={bem('transientNotifiaction', { isCheckoutLayout })}
                                                message={messages?.shippingAddressForm?.temporaryAddress?.()}
                                                type="info"
                                            />
                                        )
                                    }
                                    onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
                                        onChangeParent();
                                        onChange(ev.target.value);
                                        setEditAddressID?.(`${address.addressId}Selected`);
                                    }}
                                >
                                    {isCheckoutLayout && editable && !(address.transient || address.isTransient) && (
                                        <div
                                            className={bem('editPenButton')}
                                            onClick={() => {
                                                setEditAddressID?.(address.addressId);
                                                trackEditAddress?.();

                                                // fix: on edit: put radio checked
                                                if (address.addressId) {
                                                    onChange(`${address.addressId}`);
                                                }
                                            }}
                                        >
                                            <div className={bem('editPen')} />
                                        </div>
                                    )}

                                    {isRestricted && (
                                        <Notification
                                            className={bem('shipping-restricted')}
                                            message={messages.notifications?.shippingRestricted?.() || ''}
                                            type="error"
                                        />
                                    )}
                                </RadioButton>
                            </>
                        );
                    }}
                </FormField>
            )}
        </>
    );
};

export default AddressRadioButton;
